import React, { useMemo, memo, useState } from "react";
import {
  BookOpen,
  LogIn,
  Camera,
  Heart,
  MessageCircle,
  MapPin,
  Route,
  Info,
  FileSearch,
  X,
} from "lucide-react";
import img1 from "./images/1.png"
import img2 from "./images/2.png"
import img3 from "./images/3.png"

// Bileşenin kendisi
const KullanimRehberi = () => {
    // Modal durumu ve seçili görsel için state'ler
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    // Modal açma ve kapama fonksiyonları
    const handleImageClick = (imgUrl) => {
        setSelectedImage(imgUrl);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedImage("");
    };

    // Özellikler verisini useMemo ile sarmalayarak optimizasyon sağlıyoruz
    const features = useMemo(() => [
        {
            icon: <BookOpen className="text-indigo-400 text-4xl" />,
            title: "Mekânname Nedir?",
            desc: "Türkiye’nin tarihi ve kültürel mirasını keşfetmeni sağlayan yapay zekâ destekli platform.",
        },
        {
            icon: <LogIn className="text-pink-400 text-4xl" />,
            title: "Başlarken",
            desc: "Google ile giriş yap, ana sayfadan keşfe başla.",
        },
        {
            icon: <Camera className="text-green-400 text-4xl" />,
            title: "Fotoğraf Yükleme",
            desc: "JPG veya PNG formatında fotoğraf yükle, mekanın adını, hikayesini ve konumunu öğren.",
        },
        {
            icon: <Heart className="text-red-400 text-4xl" />,
            title: "Beğeni ve Puanlama",
            desc: "Mekanları beğenebilir, 1-5 yıldız arası puan verebilirsin.",
        },
        {
            icon: <MessageCircle className="text-yellow-400 text-4xl" />,
            title: "Yorumlar",
            desc: "Kendi yorumunu ekle, diğer kullanıcıların yorumlarını gör.",
        },
        {
            icon: <MapPin className="text-blue-400 text-4xl" />,
            title: "Harita ve Rotalar",
            desc: "Gezilecek yerleri harita üzerinde işaretle, konumuna yakın mekanları keşfet.",
        },
        {
            icon: <Route className="text-purple-400 text-4xl" />,
            title: "Kişiselleştirilmiş Öneriler",
            desc: "Gezdiğin yerlere göre özel mekan ve rota önerileri al.",
        },
        {
            icon: <Info className="text-gray-400 text-4xl" />,
            title: "Sık Sorulan Sorular",
            desc: "Yanlış tanıma, fotoğraf yükleme sorunu gibi sık karşılaşılan problemlerin çözümleri.",
        },
    ], []);

    // Adım adım rehber verisini görsellerle birlikte useMemo ile sarmalıyoruz
    const steps = useMemo(() => [
        {
            img: img1,
            title: "Fotoğraf Yükle",
            desc: "Merak ettiğin mekanın fotoğrafını yükle.",
        },
        {
            img: img2,
            title: "Sonucu Gör",
            desc: "Yapay zekâ mekanın adını, hikayesini ve konumunu hemen göstersin.",
        },
        {
            img: img3,
            title: "Notunu Al ve Yorumunu Bırak",
            desc: "Yapay zekâ mekanın adını, hikayesini ve konumunu hemen göstersin.",
        },
    ], []);

    return (
        <div className="bg-gray-850 min-h-screen text-gray-300 antialiased p-4 sm:p-8 font-sans">
            {/* Hero Bölümü */}
            <div className="bg-gray-800 rounded-3xl py-12 px-6 sm:px-12 text-center shadow-xl border border-gray-800 transform hover:scale-[1.01] transition-transform duration-300">
                <div className="mx-auto w-24 h-24 flex items-center justify-center bg-gray-800 rounded-full mb-6 shadow-md">
                    <BookOpen className="text-indigo-500 w-12 h-12" />
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-500 mb-4 leading-tight">
                    Mekânname Kullanım Rehberi
                </h1>
                <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
                    Türkiye’nin tarihi ve kültürel mirasını keşfetmek artık çok kolay.
                    Adım adım rehberimizle Mekânname’yi hemen keşfet.
                </p>
            </div>

            {/* Adım Adım Rehber Bölümü */}
            <div className="max-w-5xl mx-auto py-12 px-6">
                <h2 className="text-3xl font-bold text-white mb-8 text-center">
                    🚀 Adım Adım Rehber
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {steps.map((step, i) => (
                        <div
                            key={i}
                            className="bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-800 flex flex-col items-center text-center transform hover:scale-[1.03] transition-transform duration-300 cursor-pointer"
                            onClick={() => handleImageClick(step.img)}
                        >
                            <img
                                src={step.img}
                                alt={step.title}
                                className="w-full h-56 object-cover rounded-xl shadow-md mb-4 border-2 border-transparent hover:border-indigo-500 transition-colors duration-300"
                            />
                            <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                            <p className="text-gray-400 mt-2">{step.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Özellikler Bölümü */}
            <div className="max-w-6xl mx-auto py-12 px-6">
                <h2 className="text-3xl font-bold text-white mb-8 text-center">
                    ✨ Özellikler
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {features.map((item, index) => (
                        <div
                            key={index}
                            className="bg-gray-900 p-8 rounded-2xl shadow-lg border border-gray-800 flex flex-col items-center text-center transform hover:bg-gray-800 hover:border-purple-500 transition-all duration-300"
                        >
                            <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300">
                                {item.icon}
                            </div>
                            <h2 className="mt-4 text-xl font-semibold text-white">{item.title}</h2>
                            <p className="mt-2 text-gray-400">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <footer className="text-center py-6 border-t border-gray-800 mt-12">
                <p className="text-sm text-gray-500">&copy; 2024 Mekânname. Tüm hakları saklıdır.</p>
            </footer>

            {/* Tam Ekran Modal Penceresi */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
                    onClick={handleCloseModal}
                >
                    <div className="relative max-w-4xl max-h-full">
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-4 right-4 text-white hover:text-gray-400 transition-colors duration-200 z-10"
                        >
                            <X size={32} />
                        </button>
                        <img
                            src={selectedImage}
                            alt="Tam Ekran Görsel"
                            className="rounded-lg shadow-xl max-w-full max-h-[80vh] sm:max-h-[90vh] object-contain"
                            onClick={(e) => e.stopPropagation()} // Resme tıklandığında modalın kapanmasını engelle
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(KullanimRehberi);
